import React, { useEffect, useState, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    Slider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    SelectChangeEvent,
    Stack
} from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getWaterQualityHeatmap, WaterQualityHeatmapPoint, getLocationFromCoordinates } from '../../services/api';

// Fix for Leaflet marker icons in React
// This is needed because of how webpack processes assets
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

const colorScale = [
    { threshold: 20, color: '#00ff00', label: 'Excellent' }, // Green (excellent)
    { threshold: 40, color: '#80ff00', label: 'Very Good' }, // Light green (very good)
    { threshold: 60, color: '#ffff00', label: 'Good' },      // Yellow (good)
    { threshold: 80, color: '#ff8000', label: 'Fair' },      // Orange (fair)
    { threshold: 100, color: '#ff0000', label: 'Poor' }      // Red (poor)
];

// Get color based on quality score (lower score is better quality)
const getColor = (score: number): string => {
    for (const { threshold, color } of colorScale) {
        if (score <= threshold) {
            return color;
        }
    }
    return colorScale[colorScale.length - 1].color; // Default to worst color
};

// Component to set the map view to the user's location
interface SetViewProps {
    center: [number, number];
    zoom: number;
}

const SetView: React.FC<SetViewProps> = ({ center, zoom }) => {
    const map = useMap();
    map.setView(center, zoom);
    return null;
};

const WaterHeatmap: React.FC = () => {
    const [heatmapData, setHeatmapData] = useState<WaterQualityHeatmapPoint[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
    const [selectedLocation, setSelectedLocation] = useState<WaterQualityHeatmapPoint | null>(null);
    const [radiusFilter, setRadiusFilter] = useState<number>(10000); // 10km default
    const [mapProperty, setMapProperty] = useState<string>('qualityScore');
    const [filteredData, setFilteredData] = useState<WaterQualityHeatmapPoint[]>([]);
    const mapRef = useRef<L.Map | null>(null);

    // Load heatmap data
    useEffect(() => {
        const fetchHeatmapData = async () => {
            try {
                setLoading(true);
                const data = await getWaterQualityHeatmap();
                setHeatmapData(data);
                setFilteredData(data);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching heatmap data:', error);
                setError('Failed to load water quality heatmap data');
                setLoading(false);
            }
        };

        fetchHeatmapData();
    }, []);

    // Get user location with enhanced error handling and fallbacks
    useEffect(() => {
        const getUserLocation = async () => {
            setLoading(true);
            setError(null);

            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    async (position) => {
                        const { latitude, longitude } = position.coords;
                        setUserLocation([latitude, longitude]);

                        try {
                            // Try to get location information using our enhanced function with multiple fallbacks
                            const locationInfo = await getLocationFromCoordinates(latitude, longitude);
                            console.log('Water heatmap location data retrieved:', locationInfo);

                            // Add location info to any nearby water points
                            if (locationInfo && heatmapData.length > 0) {
                                // Find any water points that are very close to user location and update their region
                                const updatedData = heatmapData.map(point => {
                                    // Calculate distance using Haversine
                                    const lat1 = latitude * Math.PI / 180;
                                    const lat2 = point.latitude * Math.PI / 180;
                                    const deltaLat = (point.latitude - latitude) * Math.PI / 180;
                                    const deltaLng = (point.longitude - longitude) * Math.PI / 180;

                                    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
                                        Math.cos(lat1) * Math.cos(lat2) *
                                        Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
                                    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                                    const distance = 6371 * c; // Earth's radius in km * c

                                    // If within 5km, enhance with location info
                                    if (distance <= 5) {
                                        return {
                                            ...point,
                                            region: `${locationInfo.city || point.region}, ${locationInfo.state || ''}, ${locationInfo.country || ''}`.trim().replace(/, ,/g, ',')
                                        };
                                    }
                                    return point;
                                });

                                setHeatmapData(updatedData);
                                setFilteredData(updatedData.filter(point => {
                                    // Use the user's location to filter points within radius
                                    const lat1 = latitude * Math.PI / 180;
                                    const lat2 = point.latitude * Math.PI / 180;
                                    const deltaLat = (point.latitude - latitude) * Math.PI / 180;
                                    const deltaLng = (point.longitude - longitude) * Math.PI / 180;

                                    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
                                        Math.cos(lat1) * Math.cos(lat2) *
                                        Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
                                    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                                    const distance = 6371000 * c; // Earth's radius in meters * c

                                    return distance <= radiusFilter;
                                }));
                            }
                        } catch (error) {
                            console.error('Error enhancing water points with location data:', error);
                        }

                        setLoading(false);
                    },
                    (error) => {
                        console.error('Error getting user location:', error);
                        setError('Could not access your location. Please check your browser permissions and try again.');
                        // Don't use a default location, just leave map centered in its default view
                        setLoading(false);
                    },
                    {
                        enableHighAccuracy: true,
                        timeout: 10000,
                        maximumAge: 0
                    }
                );
            } else {
                setError('Geolocation is not supported by your browser. Please use a modern browser that supports geolocation.');
                setLoading(false);
            }
        };

        getUserLocation();
    }, []);

    // Filter data based on radius around user location
    useEffect(() => {
        if (!userLocation || !heatmapData.length) return;

        const [userLat, userLng] = userLocation;

        // Filter data points within the radius
        const filtered = heatmapData.filter(point => {
            // Calculate distance in kilometers using Haversine formula
            const lat1 = userLat * Math.PI / 180;
            const lat2 = point.latitude * Math.PI / 180;
            const deltaLat = (point.latitude - userLat) * Math.PI / 180;
            const deltaLng = (point.longitude - userLng) * Math.PI / 180;

            const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
                Math.cos(lat1) * Math.cos(lat2) *
                Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            const distance = 6371000 * c; // Earth's radius in meters * c

            return distance <= radiusFilter;
        });

        setFilteredData(filtered);
    }, [userLocation, heatmapData, radiusFilter]);

    // Handle radius filter change
    const handleRadiusChange = (event: Event, value: number | number[]) => {
        setRadiusFilter(value as number);
    };

    // Handle property selection change
    const handlePropertyChange = (event: SelectChangeEvent) => {
        setMapProperty(event.target.value);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert
                    severity="error"
                    action={
                        <button onClick={() => window.location.reload()}>
                            Retry
                        </button>
                    }
                >
                    {error}
                </Alert>
                <Box sx={{ mt: 2, p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Troubleshooting Tips:
                    </Typography>
                    <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                        <li>Check that your browser has permission to access your location</li>
                        <li>Ensure you have a stable internet connection</li>
                        <li>Try refreshing the page and allowing location access when prompted</li>
                        <li>If using a VPN, try disabling it temporarily</li>
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
                Global Water Quality Heat Map
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="map-property-label">Map Property</InputLabel>
                        <Select
                            labelId="map-property-label"
                            value={mapProperty}
                            label="Map Property"
                            onChange={handlePropertyChange}
                        >
                            <MenuItem value="qualityScore">Overall Quality Score</MenuItem>
                            <MenuItem value="ppm">PPM</MenuItem>
                            <MenuItem value="arsenic">Arsenic</MenuItem>
                            <MenuItem value="pH">pH</MenuItem>
                            <MenuItem value="turbidity">Turbidity</MenuItem>
                            <MenuItem value="hardness">Hardness</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography gutterBottom>Filter Radius (km)</Typography>
                    <Slider
                        value={radiusFilter / 1000} // Convert to km for display
                        onChange={handleRadiusChange}
                        min={10}
                        max={5000}
                        step={10}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value} km`}
                    />
                </Box>
            </Stack>

            <Box sx={{
                height: 500,
                width: '100%',
                '& .leaflet-container': {
                    height: '100%',
                    borderRadius: 1
                }
            }}>
                {userLocation && (
                    <MapContainer
                        center={userLocation}
                        zoom={3}
                        style={{ height: '100%', width: '100%' }}
                        ref={(map) => { mapRef.current = map; }}
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />

                        {/* Center view on user location */}
                        <SetView center={userLocation} zoom={3} />

                        {/* User location marker */}
                        <Marker position={userLocation}>
                            <Popup>
                                <Typography variant="body2">Your Location</Typography>
                            </Popup>
                        </Marker>

                        {/* Radius circle around user location */}
                        <Circle
                            center={userLocation}
                            radius={radiusFilter}
                            pathOptions={{ color: 'blue', fillColor: 'blue', fillOpacity: 0.1 }}
                        />

                        {/* Water quality data points */}
                        {filteredData.map(point => {
                            // Determine color based on selected property
                            let color = '#ff0000';
                            let value = point[mapProperty as keyof WaterQualityHeatmapPoint];

                            if (mapProperty === 'qualityScore') {
                                color = getColor(point.qualityScore);
                            } else if (mapProperty === 'ppm') {
                                // Lower PPM is better
                                color = getColor(Math.min(100, (point.ppm / 10)));
                            } else if (mapProperty === 'arsenic') {
                                // Lower arsenic is better
                                color = getColor(Math.min(100, (point.arsenic / 0.0001) * 100));
                            } else if (mapProperty === 'pH') {
                                // Closer to 7 is better
                                const pHDeviation = Math.abs(7 - point.pH) / 7;
                                color = getColor(pHDeviation * 100);
                            } else if (mapProperty === 'turbidity') {
                                // Lower turbidity is better
                                color = getColor(Math.min(100, (point.turbidity / 0.1) * 20));
                            } else if (mapProperty === 'hardness') {
                                // Moderate hardness is better
                                const hardnessDeviation = Math.abs(100 - point.hardness) / 100;
                                color = getColor(hardnessDeviation * 100);
                            }

                            return (
                                <Circle
                                    key={point.id}
                                    center={[point.latitude, point.longitude]}
                                    radius={50000} // Fixed size for visibility
                                    pathOptions={{
                                        color: color,
                                        fillColor: color,
                                        fillOpacity: 0.6
                                    }}
                                    eventHandlers={{
                                        click: () => setSelectedLocation(point)
                                    }}
                                >
                                    <Popup>
                                        <Typography variant="subtitle2">{point.region}</Typography>
                                        <Typography variant="body2">Quality Score: {point.qualityScore.toFixed(1)}</Typography>
                                        <Typography variant="body2">PPM: {point.ppm}</Typography>
                                        <Typography variant="body2">pH: {point.pH}</Typography>
                                        <Typography variant="body2">Arsenic: {point.arsenic} mg/L</Typography>
                                        <Typography variant="body2">Turbidity: {point.turbidity} NTU</Typography>
                                    </Popup>
                                </Circle>
                            );
                        })}
                    </MapContainer>
                )}
            </Box>

            {/* Legend */}
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap' }}>
                {colorScale.map(({ threshold, color, label }) => (
                    <Box key={threshold} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 16, height: 16, bgcolor: color, borderRadius: '50%' }}></Box>
                        <Typography variant="caption">{label}</Typography>
                    </Box>
                ))}
            </Box>

            {/* Selected location details */}
            {selectedLocation && (
                <Paper sx={{ mt: 2, p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        {selectedLocation.region} Water Quality Details
                    </Typography>
                    <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} flexWrap="wrap">
                        <Box sx={{ minWidth: '25%' }}>
                            <Typography variant="body2">Quality Score:</Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {selectedLocation.qualityScore.toFixed(1)}
                            </Typography>
                        </Box>
                        <Box sx={{ minWidth: '25%' }}>
                            <Typography variant="body2">PPM:</Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {selectedLocation.ppm}
                            </Typography>
                        </Box>
                        <Box sx={{ minWidth: '25%' }}>
                            <Typography variant="body2">pH:</Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {selectedLocation.pH}
                            </Typography>
                        </Box>
                        <Box sx={{ minWidth: '25%' }}>
                            <Typography variant="body2">Dissolved Oxygen:</Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {selectedLocation.dissolvedOxygen} mg/L
                            </Typography>
                        </Box>
                        <Box sx={{ minWidth: '25%' }}>
                            <Typography variant="body2">Arsenic:</Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {selectedLocation.arsenic} mg/L
                            </Typography>
                        </Box>
                        <Box sx={{ minWidth: '25%' }}>
                            <Typography variant="body2">Turbidity:</Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {selectedLocation.turbidity} NTU
                            </Typography>
                        </Box>
                        <Box sx={{ minWidth: '25%' }}>
                            <Typography variant="body2">Temperature:</Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {selectedLocation.temperature}°C
                            </Typography>
                        </Box>
                        <Box sx={{ minWidth: '25%' }}>
                            <Typography variant="body2">Hardness:</Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {selectedLocation.hardness} mg/L
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>
            )}
        </Paper>
    );
};

export default WaterHeatmap; 